import React, { useState, useContext, useEffect } from "react";

import {
  DataGrid,
  GridColDef,
  jaJP,
} from "@mui/x-data-grid";

import moment from "moment";

import { ReloadContext } from "../../context/ReloadContext";
import { postSearchKokyaku, postSearchKokyakuDairiten } from "../../api/kokyaku";
import { renderCellExpand } from "../Common/GridCellExpand";
import KokyakuEditDialog from './KokyakuEditDialog'
import ToggleVisibilityDialog from '../Common/ToggleVisibilityDialog'
import { AuthInfoContext } from "../../context/AuthContext";
import {
  KokyakuData, KokyakuDataSet, KokyakuSearchParam
} from "../../types/WebData";
import { KENGEN } from "../../const/index";

/**
 * DataGrid用にデータを編集
 *
 * @param {KokyakuDataSet[]} KokyakuDatas
 * @return {*} 
 */
const makeGridData = (KokyakuDatas: KokyakuDataSet[]) => {
  return KokyakuDatas.map((Kokyaku) => {
    // 日付フォーマット
    const updateDate = moment(Kokyaku.update_dt);
    return {
      ...Kokyaku,
      update_dt: updateDate.format("YYYY/MM/DD"),
    };
  });
};

/**
 * 全件データを１ページ分の行にsliceしている
 * 値に単位をつけるなどのデータの編集も、ここで１ページ分のみ行う
 */
const loadServerRows = (
  page: number,
  pageSize: number,
  allRows: KokyakuDataSet[]
): Promise<KokyakuDataSet[]> =>
  new Promise<KokyakuDataSet[]>((resolve) => {
    if (!allRows) return;
    resolve(
      // １ページ分にsliceした後に、データを編集するfunctionに渡している
      makeGridData(allRows.slice(page * pageSize, (page + 1) * pageSize))
    );
  });

const useQuery = (page: number, pageSize: number, allRows: any[]) => {
  const [rowCount, setRowCount] = React.useState<number | undefined>(undefined);
  const [data, setData] = React.useState<KokyakuDataSet[]>([]);

  useEffect(() => {
    let active = true;

    setRowCount(undefined);
    loadServerRows(page, pageSize, allRows).then((newRows) => {
      if (!active) {
        return;
      }
      setData(newRows);
      setRowCount(allRows.length);
    });

    return () => {
      active = false;
    };
  }, [page, pageSize, allRows]);

  return { data, rowCount };
};

interface RowsState {
  page: number;
  pageSize: number;
}

type Props = {
  searchParam: KokyakuSearchParam | null;
  setload: React.Dispatch<React.SetStateAction<boolean>>
};

/**
 * 広告主一覧コンポーネント
 *
 * @return {*} 
 */
const KokyakuGrid = ({ searchParam, setload }: Props) => {
  const [authInfo] = useContext(AuthInfoContext);
  const reloadContext = useContext(ReloadContext);
  const [kokyakuData, setKokyakuData] = useState<KokyakuData>({ data: [] });

  useEffect(() => {
    const getSearchData = async (param: KokyakuSearchParam) => {
      const result = await postSearchKokyaku(param);
      setKokyakuData(result);
      if (result?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: result.message,
        });
      }
      setload(false);
    };

    const getSearchDairitenData = async (param: KokyakuSearchParam) => {
      const result = await postSearchKokyakuDairiten(param);
      setKokyakuData(result);
      if (result?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: result.message,
        });
      }
      setload(false);
    };

    if (!searchParam) {
      if (authInfo.kengen === KENGEN.ADMIN) {
        getSearchData({ kokyaku_mei: "", jusho: "", kaisha_mei: "", is_visible: false })
      } else {
        getSearchDairitenData({ kokyaku_mei: "", jusho: "", kaisha_id: authInfo.kaisha_id, is_visible: false })
      }
    } else {
      if (authInfo.kengen === KENGEN.ADMIN) {
        getSearchData(searchParam);
      } else {
        getSearchDairitenData({ ...searchParam, kaisha_id: authInfo.kaisha_id });
      }
    }
  }, [searchParam, reloadContext?.reload]);

  const columns: GridColDef[] =
    [
      {
        field: "id",
        headerName: "ID",
        hide: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "kokyaku_mei",
        headerName: "広告主名",
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "jusho",
        headerName: "住所",
        width: 300,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "shimei",
        headerName: "担当者名",
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "kaisha_mei",
        headerName: "会社名",
        width: 150,
        hide: authInfo.kengen === KENGEN.ADMIN ? false : true,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "update_dt",
        headerName: "更新日",
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'editButton',
        headerName: "",
        width: 20,
        align: "center",
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) => (
          <KokyakuEditDialog
            kokyakuId={params.row.id} />
        )
      },
      {
        field: "toggleVisibilityDialog",
        headerName: "",
        width: 20,
        align: "center",
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) => (
          <ToggleVisibilityDialog deleteId={params.row.id} isVisible={!params.row.del_flg} tableName="kokyaku" />
        ),
      },
    ];

  // ページのstate　１ページに１０行
  const [rowsState, setRowsState] = React.useState<RowsState>({
    page: 0,
    pageSize: 10,
  });

  const { data, rowCount } = useQuery(
    rowsState.page,
    rowsState.pageSize,
    kokyakuData.data
  );

  // データの読み込みが間に合ってなくてrowCountがundefinedのままの場合の対策
  const [rowCountState, setRowCountState] = useState(rowCount || 0);
  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  return (
    <div style={{ height: 650, width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        rowCount={rowCountState}
        {...rowsState}
        rowsPerPageOptions={[10]}
        pagination
        paginationMode="server"
        sortModel={[{ field: "id", sort: "asc" }]}
        onPageChange={(page) => setRowsState((prev) => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) =>
          setRowsState((prev) => ({ ...prev, pageSize }))
        }
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      />
    </div>
  );
};

export default KokyakuGrid;